import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ComponentPropsWithRef, KeyboardEvent, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { PRESS } from '../../interaction';

export type TabsVariant = 'underline' | 'pill' | 'segmented';
export type TabsSize = 'sm' | 'md';

export interface TabItem {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Reachable by arrow keys, but Enter and Space will not open it. */
  disabled?: boolean;
  /** Rendered inside this tab's `tabpanel` when `renderPanels` is on. */
  content?: ReactNode;
}

export interface TabsProps extends Omit<ComponentPropsWithRef<'div'>, 'children' | 'onChange'> {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  variant?: TabsVariant;
  size?: TabsSize;
  /** Accessible name for the tablist. */
  label?: string;
  /** Renders the active tab's `tabpanel`. Off when the caller places panels itself. */
  renderPanels?: boolean;
  /**
   * Stretches the list and its tabs to the container. Safe only because the root
   * is then a fixed-width box — the §15/§34 gotcha's failure is `flex-1` inside
   * an *auto-width* container, where basis-0 children collapse to near zero.
   */
  fit?: boolean;
}

/** The track each variant sits in. No variant uses `w-full` on its own. */
const TRACKS: Record<TabsVariant, string> = {
  underline: 'border-b border-line-subtle',
  pill: 'gap-0.5',
  segmented: 'gap-1 rounded-xs border border-line-subtle bg-surface-muted p-1',
};

/** The travelling indicator, one per variant. Measured, so all three use rect geometry. */
const INDICATORS: Record<TabsVariant, string> = {
  underline: 'bg-primary',
  pill: 'bg-surface-inverse',
  segmented: 'border border-line-strong bg-surface',
};

const ACTIVE_LABEL: Record<TabsVariant, string> = {
  underline: 'text-heading',
  pill: 'text-inverse',
  segmented: 'text-heading',
};

const SIZES: Record<TabsSize, string> = {
  sm: 'h-8 gap-1.5 px-3 text-2xs',
  md: 'h-10 gap-2 px-4 text-xs',
};

interface IndicatorRect {
  x: number;
  y: number;
  width: number;
  height: number;
  listHeight: number;
}

export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  variant = 'underline',
  size = 'md',
  label,
  renderPanels = true,
  fit = false,
  className,
  ...rest
}: TabsProps) {
  const reduceMotion = useReducedMotion();
  const baseId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? items[0]?.value ?? '');
  const selected = value ?? uncontrolled;

  const activeIndex = items.findIndex((item) => item.value === selected);
  const [focusIndex, setFocusIndex] = useState(activeIndex >= 0 ? activeIndex : 0);
  const [rect, setRect] = useState<IndicatorRect | null>(null);

  // Keep the single tab stop on whichever tab is actually selected.
  useEffect(() => {
    if (activeIndex >= 0) setFocusIndex(activeIndex);
  }, [activeIndex]);

  // The indicator is geometry taken from the live tab, so it follows a wrapped
  // label, a font swap or a container resize rather than an assumed width.
  useLayoutEffect(() => {
    const list = listRef.current;
    const tab = activeIndex >= 0 ? tabRefs.current[activeIndex] : undefined;
    if (!list || !tab) {
      setRect(null);
      return;
    }
    const measure = () =>
      setRect({
        x: tab.offsetLeft,
        y: tab.offsetTop,
        width: tab.offsetWidth,
        height: tab.offsetHeight,
        listHeight: list.offsetHeight,
      });
    measure();

    // Absent in jsdom, so it is probed rather than assumed.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(tab);
    observer.observe(list);
    return () => observer.disconnect();
  }, [activeIndex, variant, size, fit]);

  function select(item: TabItem) {
    if (item.disabled) return;
    if (item.value === selected) return;
    if (value === undefined) setUncontrolled(item.value);
    onValueChange?.(item.value);
  }

  function moveFocus(next: number) {
    if (items.length === 0) return;
    const clamped = Math.max(0, Math.min(next, items.length - 1));
    setFocusIndex(clamped);
    tabRefs.current[clamped]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        moveFocus(focusIndex + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus(focusIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        moveFocus(items.length - 1);
        break;
      case 'Enter':
      case ' ': {
        const item = items[focusIndex];
        if (!item) return;
        event.preventDefault();
        select(item);
        break;
      }
      default:
        break;
    }
  }

  const active = activeIndex >= 0 ? items[activeIndex] : undefined;
  const panelId = active ? `${baseId}-panel-${active.value}` : undefined;

  return (
    <div {...rest} className={cn('flex min-w-0 flex-col', className)}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={label}
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        // `overflow-x-auto` matters on narrow viewports: a row of fixed-width
        // tabs has no natural wrap point, so without it a long tab set pushes
        // the whole document wider and the page itself scrolls sideways.
        // Scrolling the tab strip inside its own box is the spec's rule for
        // wide horizontal content.
        className={cn(
          'relative flex min-w-0 items-center overflow-x-auto',
          TRACKS[variant],
          fit && 'w-full',
        )}
      >
        <motion.span
          aria-hidden="true"
          initial={false}
          animate={
            rect
              ? variant === 'underline'
                ? {
                    x: rect.x,
                    width: rect.width,
                    y: rect.listHeight - 2,
                    height: 2,
                    opacity: 1,
                  }
                : { x: rect.x, y: rect.y, width: rect.width, height: rect.height, opacity: 1 }
              : { opacity: 0 }
          }
          transition={
            // `reducedMotion="user"` on MotionConfig removes the transform but
            // leaves the width/height tween running, so the whole tween is
            // shortened here rather than only its translate.
            reduceMotion
              ? { duration: 0 }
              : variant === 'underline'
                ? { type: 'spring', stiffness: 480, damping: 42 }
                : { type: 'spring', stiffness: 520, damping: 40 }
          }
          // Motion owns this element's transform and opacity, so it must not
          // also carry a CSS transition. `transition-property` defaults to
          // `all` and the reduced-motion `*` rule gives every element a
          // duration, which turns Motion's opacity write into a one-frame
          // transition: the indicator is still transparent on the frame the
          // active pill's `text-inverse` label is measured on, and #faf8f4 on
          // `--canvas` is 1.1:1.
          className={cn(
            'pointer-events-none absolute top-0 left-0 transition-none',
            INDICATORS[variant],
          )}
        />

        {items.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={item.value}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${item.value}`}
              // Kept while collapsed too: the relationship is part of the tab's
              // identity, and dropping it strands assistive tech that cached it.
              aria-controls={renderPanels ? `${baseId}-panel-${item.value}` : undefined}
              aria-selected={isActive}
              aria-disabled={item.disabled || undefined}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => {
                setFocusIndex(index);
                select(item);
              }}
              className={cn(
                'relative z-10 inline-flex items-center justify-center rounded-xs',
                'dhd-target font-display font-semibold uppercase tracking-label whitespace-nowrap',
                'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                'disabled:pointer-events-none',
                SIZES[size],
                fit && 'min-w-0 flex-1',
                variant === 'segmented' && 'rounded-xs',
                isActive ? ACTIVE_LABEL[variant] : 'text-muted hover:text-body',
                item.disabled && 'cursor-not-allowed opacity-45',
                !item.disabled && PRESS,
              )}
            >
              {item.icon ? (
                <span aria-hidden="true" className="grid size-4 shrink-0 place-items-center">
                  {item.icon}
                </span>
              ) : null}
              {item.label}
            </button>
          );
        })}
      </div>

      {renderPanels && active ? (
        <div
          role="tabpanel"
          id={panelId}
          aria-labelledby={`${baseId}-tab-${active.value}`}
          tabIndex={0}
          className={cn(
            'min-w-0 rounded-xs',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          )}
        >
          {active.content}
        </div>
      ) : null}
    </div>
  );
}
